import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import ModalContext from 'contexts/ModalContext'
import { useAppSelector } from 'hooks/reduxHooks'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import LineCrownIcon from 'LuxKit/Icons/line/LineCrownIcon'
import SolidBedIcon from 'LuxKit/Icons/solid/SolidBedIcon'
import PromotionLabel from 'LuxKit/Label/PromotionLabel'
import RadioButton from 'LuxKit/Radio/RadioButton'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { rem } from 'polished'
import type { ComponentProps } from 'react'
import { useCallback, useContext } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import focusStyle from 'styles/utils/focusStyle'
import LoyaltyStayUpgradeDetailsModal from './LoyaltyStayUpgradeDetailsModal'

const OptionItem = styled.label`
  position: relative; /* for the floating label */
  border-radius: ${props => props.theme.borderRadius.L};
  box-shadow: inset 0 0 0 var(--stroke-w, 1px) var(--stroke-colour, var(--muted));
  transition: box-shadow 200ms;
  display: grid;
  grid-template: "symbol details input" auto / min-content minmax(0, 1fr) min-content;
  gap: ${rem(16)};
  padding: ${rem(24)};

  ${mediaQueryUp.tablet} {
    padding-block: ${rem(32)};
    padding-inline-start: ${rem(32)};
    padding-inline-end: ${rem(24)};
  }

  &.variant-default {
    --accent: ${props => props.theme.palette.neutral.surface.darkForeground};
    --muted: ${props => props.theme.palette.neutral.surface.lightForeground};
  }
  &.variant-highlight {
    --accent: ${props => props.theme.palette.highlight.primary.darkBackground};
    --muted: ${props => props.theme.palette.highlight.primary.darkBackground};
  }

  &:has(input:checked) {
    --stroke-colour: var(--accent);
    --stroke-w: 2px;
  }

  &:not(:has(input:checked)) {
    cursor: pointer;
  }

  ${mediaHoverable} {
    &:hover:not(:has(input:checked)) {
      --stroke-colour: var(--accent);
      --stroke-w: 1px;
    }
  }

  &:has(:focus-visible) {
    ${focusStyle}
  }
`

const OptionInput = styled(RadioButton)`
  grid-area: input;
  align-self: center;
`

const OptionDetails = styled(Group)`
  grid-area: details;
`

const OptionSymbol = styled(Sheet)`
  grid-area: symbol;
  display: grid;
  grid-template: auto / auto;
  place-items: center;
  height: ${rem(32)};
  width: ${rem(32)};
`

const FloatingLabel = styled(PromotionLabel)`
  position: absolute;
  top: 0;
  inset-inline-start: 0;
  transform: translateY(-50%);
`

type InheritedLoyaltyStayUpgradeDetailsModalProps = Pick<ComponentProps<typeof LoyaltyStayUpgradeDetailsModal>, 'offer' | 'originPackage' | 'checkInDate' | 'checkOutDate'>

interface Props extends InheritedLoyaltyStayUpgradeDetailsModalProps {
  /**
   * @default default
   */
  variant?: 'default' | 'highlight'
  /** input name */
  name: string
  value: App.LoyaltyStayUpgradeType
  checked?: boolean
  targetPackage: App.HotelPackage
}

function LoyaltyStayUpgradeRadioOption({
  variant = 'default',
  name,
  value,
  checked,
  originPackage,
  targetPackage,
  offer,
  checkInDate,
  checkOutDate,
}: Props) {
  const intl = useIntl()
  const tier = useAppSelector(getLoyaltyDisplayTier)
  const showModal = useContext(ModalContext)
  const openRoomDetailsModal = useCallback(() => {
    showModal(<LoyaltyStayUpgradeDetailsModal
      upgradeType={value}
      originPackage={originPackage}
      targetPackage={targetPackage}
      offer={offer}
      checkInDate={checkInDate}
      checkOutDate={checkOutDate}
      hasBackAction
    />)
  }, [checkInDate, checkOutDate, offer, originPackage, showModal, targetPackage, value])

  const freeRoomUpgradeLabel = intl.formatMessage({
    id: 'nCLggSMuu6d9eGga7idH',
    defaultMessage: 'Free room upgrade',
    description: 'Label for free room upgrade option in loyalty stay upgrade selection',
  })
  const freePackageUpgradeLabel = intl.formatMessage({
    id: 'TLoJ5ypnqhQHDH1InIin',
    defaultMessage: 'Free package upgrade',
    description: 'Label for free package upgrade option in loyalty stay upgrade selection',
  })
  const viewUpgradedRoomLabel = intl.formatMessage({
    id: '6kF8-9MOSxCqz-pbFgDj',
    defaultMessage: 'View upgraded room',
    description: 'Link text to view details of upgraded room in loyalty stay upgrade',
  })
  const viewUpgradedInclusionsLabel = intl.formatMessage({
    id: '2XH5nnotOb3ydnaaKg_k',
    defaultMessage: 'View upgraded inclusions',
    description: 'Link text to view details of upgraded package inclusions in loyalty stay upgrade',
  })
  const greatValueLabel = intl.formatMessage({
    id: 'BHGAn2xD16xzC4BbSRCB',
    defaultMessage: 'Great value',
    description: 'Promotional label indicating great value on a loyalty stay upgrade option',
  })

  return <OptionItem className={`variant-${variant}`}>
    <OptionInput name={name} value={value} checked={checked} />
    <OptionSymbol background={`tier-${tier}-dark`} borderRadius="round">
      {value === 'room' && <SolidBedIcon size="S" />}
      {value === 'package' && <LineCrownIcon size="S" />}
    </OptionSymbol>
    <OptionDetails direction="vertical" horizontalAlign="start" gap={12}>
      <Group direction="vertical" gap={2}>
        <BodyText variant="medium" colour="light-foreground-alt" weight="bold">
          {value === 'room' ? freeRoomUpgradeLabel : freePackageUpgradeLabel}
        </BodyText>
        <Heading variant="heading4" colour="light-foreground-alt">
          {value === 'room' ? targetPackage.roomType.name : targetPackage.name}
        </Heading>
      </Group>
      {value === 'room' && <TextLink size="small" endIcon={<LineAngleRightIcon />} onClick={openRoomDetailsModal}>{viewUpgradedRoomLabel}</TextLink>}
      {value === 'package' && <TextLink size="small" endIcon={<LineAngleRightIcon />} onClick={openRoomDetailsModal}>{viewUpgradedInclusionsLabel}</TextLink>}
    </OptionDetails>
    {variant === 'highlight' && <FloatingLabel>{greatValueLabel}</FloatingLabel>}
  </OptionItem>
}

export default LoyaltyStayUpgradeRadioOption
